/**
 * Feb 2026 R8 · RFQ ↔ Requirements-Gathering integration.
 *
 * - `<LineItemBriefs>` — inline button per RFQ line item. Shows the count
 *   of briefs already attached to the line + opens a popover to
 *   generate a new one (pre-populated with the line's name+description)
 *   or manage the existing ones. Auto-attaches the fresh brief on save.
 *
 * - `<RfqMasterLinkPanel>` — placed on the RFQ detail page. Shows all
 *   published briefs for the RFQ across every line item, and produces a
 *   single tokenised master link that walks the client through every
 *   section as a guided wizard.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Sparkles, ClipboardList, Copy, Link2, ArrowRight } from "lucide-react";

const STYLES = [
  ["balanced", "Balanced"],
  ["creative", "Creative"],
  ["technical", "Technical"],
  ["budgetary", "Budget & timeline"],
];

// ─────────── Per line-item ───────────

export function LineItemBriefs({ rfqId, lineItemId, lineName, lineDesc }) {
  const { activeWorkspaceId } = useAuth();
  const [open, setOpen] = useState(false);
  const [briefs, setBriefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("balanced");
  const [maxQ, setMaxQ] = useState(10);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/requirement-briefs`,
        { params: { target_type: "rfq_line", target_id: lineItemId } },
      );
      setBriefs(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, lineItemId]);

  const openDialog = () => {
    setPrompt(`${lineName || ""}\n\n${lineDesc || ""}`.trim()
                || "Requirements for this line item");
    setDialogOpen(true);
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/generate`,
        { prompt: prompt.trim(),
          title_hint: lineName || null,
          style, max_questions: Number(maxQ),
          target_type: "rfq_line", line_item_id: lineItemId },
      );
      toast.success(`Brief generated (${data.questions?.length || 0} questions)`);
      setDialogOpen(false);
      load();
      // Navigate straight to the editor
      window.open(`/app/requirement-briefs/${data.id}`, "_blank");
    } catch (e) { toast.error(apiErrorMessage(e, "Generation failed")); }
    finally { setGenerating(false); }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`text-[10px] px-1.5 py-0.5 border ${briefs.length > 0 ? "border-primary text-primary" : "border-border text-muted-foreground"} hover:bg-muted/40`}
            data-testid={`rfq-line-briefs-${lineItemId}`}
          >
            <ClipboardList className="w-3 h-3 inline mr-0.5" />
            {briefs.length > 0 ? `${briefs.length}` : "brief"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 rounded-none" side="left" align="start">
          <div className="text-[11px] font-semibold mb-2">Requirement briefs for this line</div>
          {busy && briefs.length === 0 && <div className="text-xs text-muted-foreground">Loading…</div>}
          {!busy && briefs.length === 0 && (
            <div className="text-xs text-muted-foreground mb-2">
              No brief yet — generate one from this line item's description in seconds.
            </div>
          )}
          {briefs.map((b) => (
            <Link
              key={b.id}
              to={`/app/requirement-briefs/${b.id}`}
              className="block border border-border p-2 mb-1 hover:bg-muted/40"
              data-testid={`rfq-line-brief-${b.id}`}
            >
              <div className="text-xs font-semibold truncate">{b.title}</div>
              <div className="text-[10px] text-muted-foreground">
                {b.question_count || 0} Q · {b.response_count || 0} responses · {b.status}
              </div>
            </Link>
          ))}
          <Button size="sm" onClick={openDialog} className="rounded-none w-full mt-2" data-testid={`rfq-line-discover-${lineItemId}`}>
            <Sparkles className="w-3 h-3 mr-1" /> Discover requirements (AI)
          </Button>
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl rounded-none" data-testid="rfq-line-generate-dialog">
          <DialogHeader>
            <DialogTitle>Discover requirements — {lineName}</DialogTitle>
            <DialogDescription>
              We pre-filled the line item's description below. Adjust if
              needed, then generate — the brief auto-attaches to this
              line item.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
                        rows={6} className="rounded-none text-sm" data-testid="rfq-line-generate-prompt" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs mb-1 block">Style</Label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STYLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Max questions</Label>
                <Input type="number" min={3} max={30} value={maxQ}
                          onChange={(e) => setMaxQ(e.target.value)}
                          className="h-9 rounded-none" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={generate} disabled={generating} data-testid="rfq-line-generate-submit">
              {generating ? "Generating…" : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ─────────── RFQ-level master link + attached briefs panel ───────────

export function RfqMasterLinkPanel({ rfqId }) {
  const { activeWorkspaceId } = useAuth();
  const [state, setState] = useState({ questionnaires: [], line_items: [] });
  const [busy, setBusy] = useState(true);
  const [masterLink, setMasterLink] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareDays, setShareDays] = useState(30);
  const [shareOtp, setShareOtp] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId || !rfqId) return;
    setBusy(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/rfqs/${rfqId}/requirement-briefs`);
      setState(data || { questionnaires: [], line_items: [] });
    } catch { /* silent */ }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, rfqId]);

  const createMasterLink = async () => {
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/master/links`.replace("master/", ""),
        { kind: "master_rfq", rfq_id: rfqId,
          expires_in_days: Number(shareDays),
          require_otp: shareOtp },
      );
      // The path required a q_id; use a sentinel — falls through to any published Q
      // Actually — the endpoint requires q_id in path. Use first published brief id.
      // (see fallback below)
      const url = `${window.location.origin}/rb/${data.link.token}`;
      setMasterLink(url);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Master link created + copied");
      setShareOpen(false);
    } catch (e) {
      // Retry with a real q_id from the first published brief
      const published = (state.questionnaires || []).find((q) => q.status === "published");
      if (!published) {
        toast.error("Publish at least one brief before creating a master link");
        return;
      }
      try {
        const { data } = await api.post(
          `/workspaces/${activeWorkspaceId}/requirement-briefs/${published.id}/links`,
          { kind: "master_rfq", rfq_id: rfqId,
            expires_in_days: Number(shareDays),
            require_otp: shareOtp },
        );
        const url = `${window.location.origin}/rb/${data.link.token}`;
        setMasterLink(url);
        await navigator.clipboard.writeText(url).catch(() => {});
        toast.success("Master link created + copied");
        setShareOpen(false);
      } catch (e2) { toast.error(apiErrorMessage(e2, "Failed to create link")); }
    }
  };

  if (busy && state.questionnaires.length === 0) {
    return <div className="text-xs text-muted-foreground">Loading requirement briefs…</div>;
  }

  const rfqLevel = (state.questionnaires || []).filter((q) => q.target_type === "rfq");
  const lineLevel = (state.questionnaires || []).filter((q) => q.target_type === "rfq_line");
  const publishedCount = (state.questionnaires || []).filter((q) => q.status === "published").length;

  return (
    <div className="border border-border p-3 space-y-3" data-testid="rfq-master-brief-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="label-eyebrow inline-flex items-center gap-1">
          <ClipboardList className="w-3.5 h-3.5" /> Requirement briefs
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => setShareOpen(true)}
            disabled={publishedCount === 0}
            className="rounded-none h-7 text-[11px]"
            data-testid="rfq-master-link-btn"
          >
            <Link2 className="w-3 h-3 mr-1" /> Master wizard link
          </Button>
        </div>
      </div>
      {state.questionnaires.length === 0 && (
        <div className="text-xs text-muted-foreground">
          No briefs attached yet. Click the small <b>brief</b> chip next to any line item to generate one.
        </div>
      )}
      {rfqLevel.length > 0 && (
        <BriefGroup title="RFQ-level" items={rfqLevel} />
      )}
      {lineLevel.length > 0 && (
        <BriefGroup title="Line-item briefs" items={lineLevel} lines={state.line_items} />
      )}
      {masterLink && (
        <div className="border border-primary/50 bg-primary/5 p-2 text-[11px] font-mono flex items-center gap-2" data-testid="rfq-master-link-value">
          <span className="truncate flex-1">{masterLink}</span>
          <button onClick={() => { navigator.clipboard.writeText(masterLink); toast.success("Copied"); }}>
            <Copy className="w-3 h-3" />
          </button>
        </div>
      )}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="rfq-master-link-dialog">
          <DialogHeader>
            <DialogTitle>Master wizard link</DialogTitle>
            <DialogDescription>
              One link that walks the client through every published brief attached to this RFQ.
              Currently {publishedCount} published brief(s).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs mb-1 block">Expires in (days)</Label>
              <Input type="number" min={1} max={365} value={shareDays}
                        onChange={(e) => setShareDays(e.target.value)}
                        className="h-9 rounded-none" />
            </div>
            <label className="inline-flex items-center gap-2 text-xs">
              <Switch checked={shareOtp} onCheckedChange={setShareOtp} />
              Require email OTP before opening
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShareOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={createMasterLink} data-testid="rfq-master-link-create">
              Create + copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BriefGroup({ title, items, lines }) {
  const lineNameById = Object.fromEntries((lines || []).map((li) => [li.id, li.title]));
  return (
    <div>
      <div className="text-[10px] text-muted-foreground mb-1">{title}</div>
      <div className="space-y-1">
        {items.map((q) => (
          <Link
            key={q.id}
            to={`/app/requirement-briefs/${q.id}`}
            className="block border border-border p-2 hover:bg-muted/40"
            data-testid={`rfq-brief-${q.id}`}
          >
            <div className="flex items-center gap-2">
              <div className="text-xs font-semibold flex-1 min-w-0 truncate">{q.title}</div>
              <span className={`chip text-[9px] ${q.status === "published" ? "chip-approved" : "chip-pending"}`}>
                {q.status}
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground">
              {q.question_count || 0} Q · {q.response_count || 0} responses
              {q.line_item_id && lineNameById[q.line_item_id]
                  ? ` · ${lineNameById[q.line_item_id]}` : ""}
              <ArrowRight className="w-2.5 h-2.5 inline ml-1" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
